import React from 'react';
import { texteIndication } from './parseConducteur';
import QuestionsDeLaSalle from './QuestionsDeLaSalle';
import MargeConducteur, { repereCourant } from './MargeConducteur';

/**
 * Rendu du conducteur pour la scène.
 *
 * POURQUOI PAS UNE LIBRAIRIE MARKDOWN. Le projet n'en a aucune et la gouvernance
 * des dépendances vit dans `docs/architecture/LIBRARIES.md` : ajouter `marked` ou
 * `react-markdown` pour six constructions est une décision d'architecture, pas
 * un détail d'implémentation. Le conducteur n'utilise que : citations (`>`),
 * gras, italique, listes, tableaux, code inline et séparateurs.
 *
 * ⚠️ LE DÉCOUPAGE EN BLOCS VIT DANS `parseConducteur.js` (`decouperBlocs`), et
 * ce composant ne fait que les dessiner. Le curseur de lecture désigne un bloc
 * par son indice : un second découpage, ici, finirait par diverger du premier
 * et décalerait le curseur d'un paragraphe sans que rien ne le signale.
 *
 * LA SEULE DÉCISION DE DESIGN QUI COMPTE ICI : sur scène, on ne lit pas un
 * document, on cherche sa ligne en une demi-seconde en levant les yeux. Donc
 * **ce qui se DIT est en grand, en clair, pleine largeur** — et **ce qui se FAIT
 * (les indications de scène, en citation dans le markdown) est plus petit, en
 * retrait, dans une autre couleur**. C'est la seule hiérarchie dont on a besoin,
 * et elle doit survivre à un coup d'œil de trois mètres.
 *
 * Les indications marquées ⚠️ passent en ambre : ce sont les pièges (« ne pas
 * réciter la note », « dire la mise en garde AVANT les chiffres »), et ce sont
 * elles qu'on oublie précisément quand on est en train de parler.
 *
 * ⚠️ « F1 » DEVIENT UN BOUTON, LÀ OÙ IL EST ÉCRIT. Le conducteur porte cinq
 * repères `F1` — slide 1, 12, 23, 34, et une cellule du tableau des appuis —
 * et chacun tombe au milieu d'une phrase qu'on est en train de lire. Le bouton
 * du bandeau obligeait à remonter les yeux en haut de l'écran, viser, revenir :
 * trois gestes au moment précis où l'on ne doit pas décrocher. Ici, le repère
 * EST le bouton.
 * Sa sémantique est celle de la touche : il déclenche l'action de la slide
 * AFFICHÉE, pas de celle où le repère est écrit. Cliquer le F1 d'une section
 * qu'on est en train de relire en avance ne joue donc pas sa réplique — c'est
 * le même comportement qu'appuyer sur la touche, et c'est voulu.
 *
 * 24/09 — LE REPÈRE S'AFFICHE « → voix », plus « F1 ». La télécommande de
 * scène n'a que « suivant » et « précédent » : sur une slide qui porte une
 * réplique, c'est le premier appui sur « suivant » qui la joue (voir
 * `handleSlideNavigation` dans ConferencePage). Le repère dit donc le geste
 * qu'on fait vraiment. Le markdown, lui, garde `F1` : c'est le marqueur, pas
 * le libellé. Même chose pour `[[SYNTHÈSE]]`, affiché « → analyse des
 * questions ».
 *
 * ⚠️ `[[SYNTHÈSE]]` DEVIENT LE BOUTON D'ANALYSE DES QUESTIONS, pour la même
 * raison : l'analyse doit partir vers la slide 31, c'est-à-dire en plein
 * milieu d'un paragraphe qu'Adrien est en train de dire. Contrairement à F1,
 * ce bouton-ci ne dépend PAS de la slide affichée — il lance toujours la même
 * chose, et il peut donc se cliquer en avance sans risque.
 *
 * ⚠️⚠️ CHAQUE `<p>` PORTE UNE CLASSE DE TAILLE, ET C'EST OBLIGATOIRE.
 * `src/index.css` déclare `p { font-size: medium; margin-bottom: 1em }` pour
 * toute l'application. Un sélecteur d'élément bat l'héritage : sans classe, un
 * paragraphe du prompteur est donc figé à 16 px absolus et traîne 1 em de marge
 * basse, quoi que dise le conteneur. Conséquences observées en séance :
 *   · le réglage +/− de la police ne bougeait QUE le titre de section (le seul
 *     à porter une classe, `text-[0.72em]`) ;
 *   · les indications s'affichaient à 16 px alors que leur bloc demande
 *     `0.66em`, et chacune emportait une marge fantôme.
 * D'où `text-[1em]` partout : la valeur dit « hérite », la classe fait qu'elle
 * gagne. Ne retire jamais ces `text-[1em]` en les prenant pour du bruit.
 *
 * ⚠️ LES RESPIRATIONS SONT EN `em`, PAS EN REM NI EN PIXELS. Le prompteur a un
 * curseur de taille : si les marges ne suivent pas la police, réduire le texte
 * ne fait pas rentrer une slide de plus à l'écran, ça ajoute juste du blanc.
 *
 * ⚠️ PALETTE CLAIRE (08/09) — fond blanc, texte noir, sur demande d'Adrien.
 * Ce n'est pas un goût : un prompteur sombre sur un écran de retour posé face à
 * une salle éclairée oblige la pupille à se rouvrir à chaque coup d'œil, et
 * c'est ce coup d'œil-là qu'on paie. Le contraste inverse se lit à trois mètres
 * sans accommodation.
 *
 * LA HIÉRARCHIE A DÛ ÊTRE REFAITE, pas seulement inversée. Sur fond sombre elle
 * tenait dans la LUMINOSITÉ (blanc = dit, gris = fait) ; sur fond blanc, tout
 * ce qui est sombre se ressemble. Elle tient donc maintenant sur trois signaux
 * qui ne se recouvrent pas :
 *   · ce qui se DIT      → noir, pleine largeur, aucun fond ;
 *   · le GRAS dedans     → vraiment noir, sur un corps en slate-800, sinon
 *                          l'accentuation disparaît (deux noirs ne font pas
 *                          une hiérarchie) ;
 *   · ce qui se FAIT     → plus petit, en retrait, sur une bande grise avec
 *                          filet à gauche — c'est la FORME qui le distingue,
 *                          plus la couleur ;
 *   · les pièges ⚠️      → même forme, mais bande ambre. Seule couleur du
 *                          rendu, donc seule chose qui attire l'œil.
 *
 * LE CURSEUR DE LECTURE (14/09) — voir `positionCurseur` dans
 * `parseConducteur.js` pour ce qu'il mesure. Ici, trois marques sur le bloc
 * courant : un fond bleu très pâle, une barre dans la marge gauche, et un
 * filet horizontal qui descend dans le bloc au fil de la slide. La barre est
 * ce qu'on attrape de loin ; le filet dit où l'on en est DANS un long
 * paragraphe. Tout passe en ambre quand la slide aurait dû être finie — le
 * même code que le badge d'écart. Les blocs déjà passés s'estompent un peu,
 * sans devenir illisibles : le curseur peut avoir tort, et on doit pouvoir
 * lire ce qu'il croit dit.
 */

/**
 * Remplace chaque « F1 » d'un texte par un bouton cliquable.
 *
 * S'applique aux fragments DÉJÀ découpés (texte nu, intérieur d'un gras, d'un
 * italique), jamais au code inline : entre backticks, `F1` désigne la touche
 * dans une phrase technique, pas une action à déclencher.
 */
/* Les motifs qui deviennent autre chose que du texte. `[[SYNTHÈSE]]` est écrit
   tel quel dans le conducteur, à l'endroit du déroulé où l'analyse doit partir
   — c'est-à-dire bien avant la slide qui l'affiche ; `[[QUESTIONS]]` pose la
   liste classée, à la slide 34, là où Adrien la lit. Un marqueur à double
   crochet ne peut pas apparaître par accident dans une phrase. */
const ACTIONS = /\bF1\b|\[\[SYNTHÈSE\]\]|\[\[QUESTIONS\]\]/g;

/**
 * Le bouton qui lance l'analyse des questions de la salle.
 *
 * ⚠️ IL PASSE PAR UN ÉVÉNEMENT, et c'est le même procédé que F1 (voir
 * ConferencePage) : le compteur du bandeau détient déjà l'état de la
 * génération — en cours, durée, alerte — et c'est lui qui l'affiche. Faire
 * redescendre cet état jusqu'ici, au fond du rendu du conducteur, n'ajouterait
 * rien qu'on ne voie déjà en haut de l'écran.
 *
 * 21/09 — GROS, ET SEUL SUR SA LIGNE. Le conducteur portait cinq lignes
 * d'explication autour de lui (ce que fait la synthèse, combien de temps,
 * où lire l'avancement) ; Adrien les a fait retirer : le conducteur est un
 * prompteur, on le lit en parlant, et ces lignes-là n'ont jamais été dites.
 * Il ne reste que `[[SYNTHÈSE]]`, donc le bouton est désormais le seul objet
 * de son paragraphe — d'où la taille. Il doit se trouver d'un coup d'œil, en
 * scène, sans lâcher la salle des yeux. Ce qu'il fait se lit dans son
 * infobulle, et son avancement dans le bandeau.
 */
function BoutonSynthese({ cle }) {
    return (
        <button
            key={cle}
            onClick={() => window.dispatchEvent(new CustomEvent('salle:synthese'))}
            title="Ici, un appui sur « suivant » lance l'analyse des questions de la salle : classement, synthèse et voix (20 à 60 s). Cliquer la lance tout de suite. L'avancement s'affiche dans le bandeau, en haut."
            className="inline-block my-[0.4em] px-[1em] py-[0.45em] rounded-xl bg-violet-600 text-white font-bold text-[1.45em] leading-tight shadow-md hover:bg-violet-700 active:bg-violet-800"
        >
            → analyse des questions
        </button>
    );
}

function morcelerF1(texte, cle, onF1) {
    ACTIONS.lastIndex = 0;
    if (!ACTIONS.test(texte)) return texte;
    const morceaux = [];
    const re = new RegExp(ACTIONS.source, 'g');
    let dernier = 0;
    let m;
    while ((m = re.exec(texte)) !== null) {
        if (m.index > dernier) morceaux.push(texte.slice(dernier, m.index));
        if (m[0] === '[[QUESTIONS]]') {
            morceaux.push(<QuestionsDeLaSalle key={`${cle}-q-${m.index}`} />);
        } else if (m[0] !== 'F1') {
            morceaux.push(<BoutonSynthese key={`${cle}-syn-${m.index}`} cle={`${cle}-syn-${m.index}`} />);
        } else if (onF1) {
            morceaux.push(
                <button
                    key={`${cle}-f1-${m.index}`}
                    onClick={onF1}
                    title="Ici, un appui sur « suivant » joue la voix IA de la slide affichée. Cliquer la joue tout de suite."
                    /* Rembourrage en `em` : le bouton suit le curseur de taille du
                       prompteur, et reste petit à l'intérieur d'une indication. */
                    className="inline-block align-baseline mx-[0.12em] px-[0.5em] py-[0.08em] rounded bg-amber-500 text-white font-bold shadow-sm hover:bg-amber-600 active:bg-amber-700"
                >
                    → voix
                </button>
            );
        } else {
            morceaux.push(m[0]);   // pas d'action F1 branchée : on laisse le texte
        }
        dernier = m.index + m[0].length;
    }
    if (dernier < texte.length) morceaux.push(texte.slice(dernier));
    return morceaux;
}

/** Gras, italique et code inline, sans jamais injecter de HTML. */
function enrichir(texte, cle, onF1) {
    const morceaux = [];
    // Un seul passage, trois motifs : l'ordre évite qu'un `**` soit avalé par `*`.
    const re = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g;
    let dernier = 0;
    let m;
    while ((m = re.exec(texte)) !== null) {
        if (m.index > dernier) morceaux.push(morcelerF1(texte.slice(dernier, m.index), `${cle}-nu-${m.index}`, onF1));
        const t = m[0];
        if (t.startsWith('**')) {
            morceaux.push(<strong key={`${cle}-${m.index}`} className="font-bold text-black">{morcelerF1(t.slice(2, -2), `${cle}-${m.index}`, onF1)}</strong>);
        } else if (t.startsWith('`')) {
            morceaux.push(<code key={`${cle}-${m.index}`} className="px-1 rounded bg-slate-100 border border-slate-200 text-sky-800 text-[0.85em]">{t.slice(1, -1)}</code>);
        } else {
            morceaux.push(<em key={`${cle}-${m.index}`} className="italic">{morcelerF1(t.slice(1, -1), `${cle}-${m.index}`, onF1)}</em>);
        }
        dernier = m.index + t.length;
    }
    if (dernier < texte.length) morceaux.push(morcelerF1(texte.slice(dernier), `${cle}-fin`, onF1));
    return morceaux;
}

function Tableau({ lignes, onF1 }) {
    const cellules = (l) => l.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
    // La deuxième ligne d'un tableau markdown est le séparateur : on la saute.
    const corps = lignes.filter((l) => !/^\s*\|[\s:|-]+\|\s*$/.test(l));
    const [entete, ...reste] = corps;
    return (
        <div className="my-[0.6em] overflow-x-auto">
            <table className="w-full border-collapse text-[0.8em]">
                <thead>
                    <tr>
                        {cellules(entete).map((c, i) => (
                            <th key={i} className="border border-slate-300 bg-slate-100 px-3 py-2 text-left font-semibold text-slate-700">
                                {enrichir(c, `th-${i}`, onF1)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {reste.map((l, i) => (
                        <tr key={i}>
                            {cellules(l).map((c, j) => (
                                <td key={j} className="border border-slate-200 px-3 py-2 align-top text-slate-800">
                                    {enrichir(c, `td-${i}-${j}`, onF1)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/**
 * Une indication de scène : le bloc `>` du markdown.
 *
 * ⚠️ LES BRÈVES SONT COMPACTES, ET C'EST LE RÉGLAGE QUI FAIT TENIR UNE SLIDE
 * SUR UN ÉCRAN. « *Temps.* », « *Point 2.* », « *Laisser lire.* » sont la
 * moitié des indications du conducteur. À deux respirations plus deux paddings
 * chacune, elles poussaient hors de l'écran le texte qu'on doit justement
 * lire — un mot coûtait cinquante pixels. En dessous de 80 signes et d'un seul
 * paragraphe, l'indication perd donc son rembourrage vertical et tient sur sa
 * ligne. Elle garde son filet à gauche : c'est lui qui dit « ceci se fait, ne
 * se dit pas », et c'est la seule chose qu'on lise de cette indication en
 * levant les yeux.
 *
 * ⚠️ LES LIGNES D'UN MÊME PARAGRAPHE SONT RECOLLÉES AVANT D'ÊTRE ENRICHIES.
 * Le conducteur est enroulé à 80 colonnes, et ses italiques traversent donc les
 * retours à la ligne : « *Adrien monte… / Puis **F1**.* » ouvre l'italique sur
 * une ligne et le ferme sur la suivante. Enrichi ligne par ligne, aucun `*` ne
 * trouvait sa paire — et le présentateur lisait des astérisques.
 */
function Indication({ lignes, cle, onF1 }) {
    const texte = texteIndication(lignes);
    const alerte = texte.includes('⚠️') || texte.includes('⛔️');
    // Un paragraphe = des lignes contiguës ; la ligne vide reste une respiration.
    const paragraphes = texte.split(/\n\s*\n/).map((p) => p.split('\n').join(' ').trim()).filter(Boolean);
    const breve = paragraphes.length === 1 && texte.trim().length <= 80;
    return (
        <div
            className={`border-l-4 pl-[0.8em] text-[0.66em] leading-snug ${breve ? 'my-[0.4em] py-0' : 'my-[0.6em] py-[0.4em]'
                } ${alerte
                    ? 'border-amber-500 bg-amber-50 text-amber-900'
                    : 'border-slate-300 bg-slate-50 text-slate-500'
                }`}
        >
            {paragraphes.map((para, i) => {
                // Une énumération dans une indication (« 1. Trop frontal. 2. … »)
                // garde ses retours : recollée en un bloc, elle devient illisible
                // au coup d'œil, et c'est le seul usage qu'on en fait.
                if (/^\d+\.\s/.test(para)) {
                    return (
                        <div key={i} className={i ? 'mt-[0.5em]' : ''}>
                            {para.split(/(?=\s\d+\.\s)/).map((item, j) => (
                                <div key={j}>{enrichir(item.trim(), `${cle}-${i}-${j}`, onF1)}</div>
                            ))}
                        </div>
                    );
                }
                return (
                    <p key={i} className={`my-0 text-[1em] ${i ? 'mt-[0.5em]' : ''}`}>
                        {enrichir(para, `${cle}-${i}`, onF1)}
                    </p>
                );
            })}
        </div>
    );
}

function rendreBloc(bloc, i, onF1) {
    switch (bloc.type) {
        case 'separateur':
            return <hr className="my-[0.8em] border-slate-200" />;
        case 'titre':
            // Il s'affichait avec ses dièses, comme un paragraphe à dire.
            return (
                <h3 className="mt-[1.4em] mb-[0.4em] text-[0.72em] font-bold uppercase tracking-wider text-slate-500">
                    {enrichir(bloc.texte, `h-${i}`, onF1)}
                </h3>
            );
        case 'indication':
            return <Indication cle={`i-${i}`} lignes={bloc.lignes} onF1={onF1} />;
        case 'tableau':
            return <Tableau lignes={bloc.lignes} onF1={onF1} />;
        case 'liste':
            return (
                <ul className="my-[0.5em] ml-[1.4em] list-disc space-y-[0.2em] text-[1em] text-slate-800 marker:text-slate-400">
                    {bloc.items.map((b, j) => <li key={j}>{enrichir(b, `li-${i}-${j}`, onF1)}</li>)}
                </ul>
            );
        default:
            return (
                <p className="my-[0.55em] leading-[1.45] text-[1em] text-slate-800">
                    {enrichir(bloc.texte, `p-${i}`, onF1)}
                </p>
            );
    }
}

/**
 * @param blocs          les blocs de la section (`decouperBlocs`)
 * @param marge          les repères de la section (`:: …`), `[{texte, bloc}]`
 * @param curseur        `{bloc, fraction, depasse}` sur la section affichée, sinon null
 * @param refFilet       ref posée sur le filet — le prompteur le mesure pour défiler
 * @param onChoisirBloc  clic sur un bloc = « je suis ici » ; absent hors section affichée
 *
 * ⚠️ L'ENVELOPPE DE CHAQUE BLOC N'A NI PADDING NI BORDURE, exprès : les marges
 * verticales de son contenu la traversent donc et se fondent avec celles des
 * voisins, comme avant qu'elle existe. Lui donner un rembourrage ferait
 * doubler toutes les respirations du conducteur. Les marques du curseur sont
 * en position absolue pour la même raison ; `isolate` garde le fond pâle
 * derrière le texte sans le faire passer derrière la page.
 */
export default function RenduConducteur({ blocs = [], marge = [], onF1, curseur = null, refFilet, onChoisirBloc }) {
    const courant = repereCourant(marge, curseur);
    // Les repères de chaque bloc. Un repère écrit après le dernier bloc
    // rejoint celui-ci plutôt que de disparaître.
    const reperesDe = {};
    marge.forEach((n, i) => {
        const b = Math.min(n.bloc, blocs.length - 1);
        (reperesDe[b] = reperesDe[b] || []).push({ texte: n.texte, i });
    });
    return (
        <div>
            {blocs.map((bloc, i) => {
                const actif = curseur?.bloc === i;
                const passe = curseur != null && i < curseur.bloc;
                const ambre = actif && curseur.depasse;
                return (
                    <div
                        key={i}
                        onClick={onChoisirBloc ? () => onChoisirBloc(i) : undefined}
                        className={`relative isolate transition-opacity duration-300 ${passe ? 'opacity-50' : ''} ${onChoisirBloc ? 'cursor-pointer' : ''}`}
                    >
                        {actif && (
                            <>
                                <span
                                    aria-hidden
                                    className={`pointer-events-none absolute -z-10 -inset-x-[0.35em] -inset-y-[0.12em] rounded ${ambre ? 'bg-amber-50' : 'bg-sky-50'}`}
                                />
                                <span
                                    aria-hidden
                                    className={`pointer-events-none absolute -left-[0.8em] top-0 bottom-0 w-[0.2em] rounded-full ${ambre ? 'bg-amber-500' : 'bg-sky-500'}`}
                                />
                                {/* La transition dure autant que le pas du chrono
                                    (500 ms) : le filet glisse au lieu de sauter. */}
                                <span
                                    ref={refFilet}
                                    aria-hidden
                                    className={`pointer-events-none absolute -left-[0.8em] right-0 h-[2px] transition-[top] duration-500 ease-linear ${ambre ? 'bg-amber-500/70' : 'bg-sky-500/70'}`}
                                    style={{ top: `${curseur.fraction * 100}%` }}
                                />
                            </>
                        )}
                        <MargeConducteur notes={reperesDe[i]} courant={courant} />
                        {rendreBloc(bloc, i, onF1)}
                    </div>
                );
            })}
        </div>
    );
}
